import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Alert, Modal, Badge, InputGroup, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import Footer from "../layouts/Footer";
import { Link } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default function ScheduledMessageSender() {
  // États
  const [imports, setImports] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Filtres
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('dateEnvoiComplet');
  const [sortDirection, setSortDirection] = useState('desc');

  // ✅ Ref pour éviter le double fetch
  const initDoneRef = useRef(false);

  const API_URL = "/bulk-list";

  // ============================================
  // CHARGEMENT DES IMPORTS
  // ============================================
  const formatImportsData = (data) => {
    return data.map((item, index) => ({
      id: index + 1,
      bulk_id: item.bulk_id,
      business_id: item.business_id,
      sender: item.sender || "Non défini",
      date_envoi: item.date_import || "Non défini",
      texte: `Import ${item.bulk_id}`,
      destinataires: "Multiple",
      type: "SMS Pro",
      dateEnvoiComplet: item.date_import || "Non défini",
      statut: item.date_envoi ? "Programmé" : "Importé",
      cout: "0.0",
      dateCreation: new Date().toLocaleString('fr-FR'),
      reference: item.bulk_id
    }));
  };

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const fetchImports = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Token d'authentification manquant");

        const response = await apiClient.get(API_URL);
        const data = response.data;

        if (!data.data || !Array.isArray(data.data)) {
          throw new Error("Structure de données invalide");
        }

        const formattedImports = formatImportsData(data.data);
        setImports(formattedImports);
        setScheduledMessages(formattedImports);
      } catch (err) {
        console.error("❌ Erreur chargement imports:", err);
        setError(`Erreur: ${err.message}`);
        setImports([]);
        setScheduledMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchImports();
  }, []);

  const handleReload = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Token d'authentification manquant");

      const response = await apiClient.get(API_URL);
      const data = response.data;
      const formattedImports = formatImportsData(data.data);
      setImports(formattedImports);
      setScheduledMessages(formattedImports);
    } catch (err) {
      console.error("❌ Erreur rechargement:", err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = scheduledMessages.filter(item => {
      const matchText = !filterText ||
        item.texte.toLowerCase().includes(filterText.toLowerCase()) ||
        item.destinataires.toLowerCase().includes(filterText.toLowerCase()) ||
        item.type.toLowerCase().includes(filterText.toLowerCase()) ||
        item.statut.toLowerCase().includes(filterText.toLowerCase()) ||
        item.reference.toLowerCase().includes(filterText.toLowerCase()) ||
        String(item.bulk_id).toLowerCase().includes(filterText.toLowerCase());

      const matchStatus = filterStatus === 'all' || item.statut === filterStatus;

      return matchText && matchStatus;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'dateEnvoiComplet' && aValue !== "Non programmé" && bValue !== "Non programmé") {
        try {
          aValue = new Date(aValue.split('/').reverse().join('-'));
          bValue = new Date(bValue.split('/').reverse().join('-'));
        } catch {}
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [scheduledMessages, filterText, filterStatus, sortField, sortDirection]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = scheduledMessages.length;
    const scheduled = scheduledMessages.filter(m => m.statut === "Programmé").length;
    const imported = scheduledMessages.filter(m => m.statut === "Importé").length;
    return { total, scheduled, imported };
  }, [scheduledMessages]);

  // ============================================
  // ANNULATION
  // ============================================
  const cancelScheduledMessage = (messageId) => {
    if (window.confirm("Êtes-vous sûr de vouloir annuler cet import ?")) {
      setScheduledMessages(prev => prev.filter(msg => msg.id !== messageId));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th
      className="imports-sortable-header"
      onClick={() => handleSort(field)}
    >
      <span className="imports-sortable-content">
        {children}
        {sortField === field && (
          <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-line imports-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="imports-page main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="imports-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="imports-header-icon">
              <i className="ri-file-list-3-line"></i>
            </div>
            <div>
              <h3 className="imports-header-title mb-1">Imports de messages</h3>
              <p className="imports-header-subtitle mb-0">
                Gérez vos imports SMS programmés et prêts à être envoyés
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button className="imports-btn-secondary" onClick={handleReload} disabled={loading}>
              <i className={`ri-refresh-line ${loading ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>
            <Button as={Link} to="/apps/EnvoieMasse" className="imports-btn-primary">
              <i className="ri-add-line"></i>
              <span>Nouvel import</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ALERTS ========== */}
      {showSuccess && (
        <div className="imports-alert imports-alert-success mb-4">
          <i className="ri-checkbox-circle-fill"></i>
          <div>
            <div className="fw-bold">Opération réussie</div>
            <div className="small opacity-75">L'import a été mis à jour avec succès</div>
          </div>
        </div>
      )}

      {error && (
        <div className="imports-alert imports-alert-danger mb-4">
          <i className="ri-error-warning-fill"></i>
          <div className="flex-grow-1">
            <div className="fw-bold">Erreur de chargement</div>
            <div className="small opacity-75">{error}</div>
          </div>
          <button className="imports-alert-btn" onClick={handleReload}>
            <i className="ri-refresh-line"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="imports-stat-card imports-stat-blue">
            <div className="imports-stat-icon-wrapper imports-stat-icon-blue">
              <i className="ri-file-list-3-line"></i>
            </div>
            <div className="imports-stat-content">
              <div className="imports-stat-label">Total imports</div>
              <div className="imports-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="imports-stat-card imports-stat-amber">
            <div className="imports-stat-icon-wrapper imports-stat-icon-amber">
              <i className="ri-time-line"></i>
            </div>
            <div className="imports-stat-content">
              <div className="imports-stat-label">Programmés</div>
              <div className="imports-stat-value">{stats.scheduled.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="imports-stat-card imports-stat-gray">
            <div className="imports-stat-icon-wrapper imports-stat-icon-gray">
              <i className="ri-inbox-archive-line"></i>
            </div>
            <div className="imports-stat-content">
              <div className="imports-stat-label">Importés</div>
              <div className="imports-stat-value">{stats.imported.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="imports-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="imports-search-wrapper">
              <i className="ri-search-line imports-search-icon"></i>
              <input
                type="text"
                className="imports-search-input"
                placeholder="Rechercher un import (ID, référence, statut...)"
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="imports-search-clear" onClick={() => setFilterText('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="imports-select"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">Tous les statuts</option>
              <option value="Programmé">⏰ Programmés</option>
              <option value="Importé">📥 Importés</option>
            </select>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="imports-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="imports-table-card">
        {loading ? (
          <div className="imports-loading">
            <div className="imports-loading-spinner"></div>
            <div className="imports-loading-text">Chargement des imports...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="imports-table">
                <thead>
                  <tr>
                    <SortableHeader field="bulk_id">
                      <i className="ri-hashtag me-1"></i>ID Import
                    </SortableHeader>
                    <SortableHeader field="business_id">
                      <i className="ri-building-line me-1"></i>Business
                    </SortableHeader>
                    <SortableHeader field="sender">
                      <i className="ri-user-line me-1"></i>Sender
                    </SortableHeader>
                    <SortableHeader field="dateEnvoiComplet">
                      <i className="ri-calendar-line me-1"></i>Date d'import
                    </SortableHeader>
                    <SortableHeader field="statut">
                      <i className="ri-flag-line me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="type">
                      <i className="ri-price-tag-3-line me-1"></i>Type
                    </SortableHeader>
                    <SortableHeader field="reference">
                      <i className="ri-file-copy-line me-1"></i>Référence
                    </SortableHeader>
                    <th className="text-end" style={{ width: '120px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item) => {
                      const isScheduled = item.statut === "Programmé";
                      return (
                        <tr key={item.id} className="imports-row">
                          <td>
                            <code className="imports-code">{item.bulk_id}</code>
                          </td>
                          <td>
                            <span className="imports-badge imports-badge-info">
                              <i className="ri-building-line"></i>
                              {item.business_id}
                            </span>
                          </td>
                          <td>
                            <span className="imports-sender">
                              <i className="ri-user-line"></i>
                              {item.sender}
                            </span>
                          </td>
                          <td>
                            <div className="imports-date-cell">
                              <div className="imports-date-main">
                                {item.dateEnvoiComplet}
                              </div>
                              <div className="imports-date-ago">
                                <i className="ri-time-line"></i>
                                Créé le {item.date_envoi}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`imports-status-badge ${isScheduled ? 'imports-status-scheduled' : 'imports-status-imported'}`}>
                              <i className={isScheduled ? 'ri-time-line' : 'ri-inbox-archive-line'}></i>
                              {item.statut}
                            </span>
                          </td>
                          <td>
                            <span className="imports-badge imports-badge-light">
                              {item.type}
                            </span>
                          </td>
                          <td>
                            <code className="imports-ref">{item.reference}</code>
                          </td>
                          <td className="text-end">
                            <div className="imports-actions">
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Voir détails</Tooltip>}
                              >
                                <Link
                                  to={`/apps/import-details/${item.bulk_id}`}
                                  className="imports-action-btn imports-action-view"
                                >
                                  <i className="ri-eye-line"></i>
                                </Link>
                              </OverlayTrigger>
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Annuler</Tooltip>}
                              >
                                <button
                                  className="imports-action-btn imports-action-delete"
                                  onClick={() => cancelScheduledMessage(item.id)}
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="imports-empty-state">
                          <div className="imports-empty-icon">
                            <i className="ri-inbox-line"></i>
                          </div>
                          <h5 className="imports-empty-title">Aucun import trouvé</h5>
                          <p className="imports-empty-text">
                            {filterText || filterStatus !== 'all'
                              ? "Aucun import ne correspond à vos critères"
                              : "Créez votre premier import pour commencer"}
                          </p>
                          {filterText || filterStatus !== 'all' ? (
                            <Button
                              className="imports-btn-empty-reset"
                              onClick={() => { setFilterText(''); setFilterStatus('all'); }}
                            >
                              <i className="ri-refresh-line me-2"></i>
                              Réinitialiser les filtres
                            </Button>
                          ) : (
                            <Link to="/apps/EnvoieMasse" className="imports-btn-empty-reset">
                              <i className="ri-add-line me-2"></i>
                              Créer un import
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ========== PAGINATION ========== */}
            {filteredAndSortedItems.length > 0 && (
              <div className="imports-pagination">
                <div className="imports-pagination-info">
                  <i className="ri-information-line"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                </div>
                {totalPages > 1 && (
                  <div className="imports-pagination-controls">
                    <button
                      className="imports-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      <i className="ri-arrow-left-double-line"></i>
                    </button>
                    <button
                      className="imports-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>

                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const pageNum = currentPage <= 3
                        ? index + 1
                        : currentPage >= totalPages - 2
                          ? totalPages - 4 + index
                          : currentPage - 2 + index;

                      if (pageNum > 0 && pageNum <= totalPages) {
                        return (
                          <button
                            key={pageNum}
                            className={`imports-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}

                    <button
                      className="imports-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    <button
                      className="imports-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      <i className="ri-arrow-right-double-line"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .imports-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .imports-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .imports-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .imports-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .imports-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .imports-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .imports-btn-primary, .imports-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
        }
        .imports-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .imports-btn-primary:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .imports-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .imports-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .imports-btn-secondary:disabled {
          opacity: 0.6;
        }

        /* ========== ALERTS ========== */
        .imports-alert {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 20px;
          border-radius: 12px;
          border: none;
        }
        .imports-alert > i {
          font-size: 1.5rem;
          flex-shrink: 0;
        }
        .imports-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .imports-alert-success > i {
          color: #10b981;
        }
        .imports-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .imports-alert-danger > i {
          color: #ef4444;
        }
        .imports-alert-btn {
          background: white;
          border: 1px solid #fecaca;
          color: #991b1b;
          padding: 8px 14px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.8rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .imports-alert-btn:hover {
          background: #991b1b;
          color: white;
          border-color: #991b1b;
        }

        /* ========== STATS ========== */
        .imports-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .imports-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .imports-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .imports-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .imports-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .imports-stat-icon-gray  { background: #f1f5f9; color: #64748b; }
        .imports-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .imports-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .imports-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .imports-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .imports-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .imports-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .imports-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
        }
        .imports-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .imports-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .imports-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .imports-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          transition: all 0.2s;
          cursor: pointer;
        }
        .imports-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .imports-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .imports-table {
          width: 100%;
          font-size: 0.875rem;
        }
        .imports-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .imports-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .imports-row {
          transition: background-color 0.15s;
        }
        .imports-row:hover {
          background-color: #f8fafc;
        }
        .imports-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .imports-sortable-header:hover {
          color: #3b82f6;
        }
        .imports-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .imports-sort-icon {
          color: #3b82f6;
          font-size: 0.9rem;
        }

        /* Code */
        .imports-code {
          font-size: 0.8rem;
          color: #1e40af;
          background: #eff6ff;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 700;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }
        .imports-ref {
          font-size: 0.75rem;
          color: #64748b;
          background: #f1f5f9;
          padding: 3px 8px;
          border-radius: 5px;
        }

        /* Badges */
        .imports-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .imports-badge-info {
          background: #cffafe;
          color: #0891b2;
        }
        .imports-badge-light {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        /* Sender */
        .imports-sender {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.8rem;
          color: #334155;
          font-weight: 500;
        }
        .imports-sender i {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        /* Date cell */
        .imports-date-cell {
          line-height: 1.3;
        }
        .imports-date-main {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.82rem;
          font-variant-numeric: tabular-nums;
        }
        .imports-date-ago {
          font-size: 0.7rem;
          color: #94a3b8;
          margin-top: 2px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        /* Status badge */
        .imports-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 11px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.72rem;
          white-space: nowrap;
        }
        .imports-status-scheduled {
          background: #fef3c7;
          color: #d97706;
        }
        .imports-status-imported {
          background: #f1f5f9;
          color: #64748b;
        }

        /* Actions */
        .imports-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .imports-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.95rem;
          text-decoration: none;
        }
        .imports-action-view:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }
        .imports-action-delete:hover {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .imports-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .imports-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .imports-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .imports-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .imports-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .imports-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .imports-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .imports-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          text-decoration: none;
          transition: all 0.2s;
        }
        .imports-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .imports-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .imports-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .imports-pagination-info strong {
          color: #0f172a;
        }
        .imports-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .imports-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .imports-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .imports-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .imports-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .imports-header {
            padding: 20px;
          }
          .imports-header-title {
            font-size: 1.2rem;
          }
          .imports-pagination {
            flex-direction: column;
          }
          .imports-stat-value {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
}